import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ProgramsCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const badgeRef = useScrollAnimation({ delay: 0 });
  const leftContentRef = useScrollAnimation({ delay: 100 });
  const formRef = useScrollAnimation({ delay: 200 });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector(
      'textarea[name="message"]',
    ) as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;

    setLoading(true);
    // Convert FormData to URLSearchParams safely
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      await fetch(
        'https://readdy.ai/api/form/d6kqu0iqoe30lj0v7ln0',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: data.toString(),
        },
      );
      setSubmitted(true);
    } catch (err) {
      // Optional: log error for debugging
      console.error('Form submission error:', err);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking-form" className="py-20 px-4 relative overflow-hidden" style={{ background: '#ffffff' }}>
      {/* Subtle decorative background */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 10% 50%, #B8860B 0%, transparent 50%), radial-gradient(circle at 90% 20%, #8B0000 0%, transparent 50%)' }}></div>

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Guarantee Badge */}
        <div className="flex justify-center mb-12">
          <div
            ref={badgeRef}
            className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl"
            style={{
              background: 'rgba(184,134,11,0.08)',
              border: '2px solid #B8860B',
            }}
          >
            <div
              className="w-12 h-12 flex items-center justify-center rounded-full"
              style={{
                background: 'linear-gradient(135deg, #B8860B, #F5D76E)',
              }}
            >
              <i className="ri-shield-check-fill text-2xl text-black"></i>
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-black leading-tight">
                100% Satisfaction Guarantee
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                If you're not happy after 2 weeks, we'll refund every penny —
                no questions asked
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side — Copy */}
          <div ref={leftContentRef}>
            <div
              className="inline-block px-3 py-1 rounded-full mb-4"
              style={{
                background: 'rgba(139,0,0,0.08)',
                border: '1px solid rgba(139,0,0,0.2)',
              }}
            >
              <p
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: '#8B0000' }}
              >
                Your Transformation Starts Here
              </p>
            </div>

            <h2 className="text-4xl sm:text-5xl font-extrabold text-black mb-6 leading-tight">
              Your transformation starts with{' '}
              <span
                style={{
                  background: 'linear-gradient(90deg, #B8860B, #F5D76E)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                one conversation
              </span>
            </h2>

            <p className="text-base text-gray-600 leading-relaxed mb-8">
              No commitment. No pressure. Just an honest,
              straightforward conversation about where you are, where you
              want to be, and how we'll get you there — faster than you
              thought possible.
            </p>

            <div className="space-y-4 mb-8">
              {[
                {
                  icon: 'ri-user-search-line',
                  text: "We'll assess your current fitness level & lifestyle",
                },
                {
                  icon: 'ri-roadmap-line',
                  text: 'Get a personalized plan recommendation tailored to your goals',
                },
                {
                  icon: 'ri-question-answer-line',
                  text: 'Ask anything — no question is too small or too big',
                },
                {
                  icon: 'ri-calendar-check-line',
                  text: 'Walk away with clarity and a clear next step',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg"
                    style={{
                      background: 'linear-gradient(135deg, #B8860B, #8B0000)',
                    }}
                  >
                    <i className={`${item.icon} text-lg text-white`}></i>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed pt-2">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Guarantee Block */}
            <div
              className="p-5 rounded-xl"
              style={{
                background: 'rgba(184,134,11,0.06)',
                border: '1px solid rgba(184,134,11,0.25)',
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full"
                  style={{ background: '#B8860B' }}
                >
                  <i className="ri-shield-star-fill text-base text-black"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-black mb-1">
                    Our Iron-Clad Promise
                  </h4>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    If you're not completely satisfied after your first 2
                    weeks, we'll refund every single penny — no questions,
                    no hassle, no hard feelings. That's how confident we are
                    in what we do.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side — Form */}
          <div
            ref={formRef}
            className="rounded-2xl p-8 lg:p-10"
            style={{
              background: '#f9f6f0',
              border: '2px solid rgba(184,134,11,0.3)',
            }}
          >
            {submitted ? (
              <div className="text-center py-12">
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-5"
                  style={{
                    background: 'linear-gradient(135deg, #B8860B, #F5D76E)',
                  }}
                >
                  <i className="ri-check-double-line text-3xl text-black"></i>
                </div>
                <h3 className="text-2xl font-extrabold text-black mb-3">
                  You're In!
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Benny will reach out within 24 hours to schedule your
                  free consultation. Check your email and phone — we move
                  fast.
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-extrabold text-black mb-2">
                    Book Your Free Call
                  </h3>
                  <p className="text-xs text-gray-500">
                    30 minutes that could change everything
                  </p>
                </div>

                <form
                  data-readdy-form
                  id="consultation_form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-xs font-bold mb-2 uppercase tracking-wide"
                        style={{ color: '#B8860B' }}
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="John"
                        className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 bg-white"
                        style={{
                          border: '1px solid rgba(184,134,11,0.3)',
                          color: '#1a1a1a',
                        }}
                        onFocus={(e) =>
                          (e.currentTarget.style.borderColor = '#B8860B')
                        }
                        onBlur={(e) =>
                          (e.currentTarget.style.borderColor =
                            'rgba(184,134,11,0.3)')
                        }
                      />
                    </div>
                    <div>
                      <label
                        className="block text-xs font-bold mb-2 uppercase tracking-wide"
                        style={{ color: '#B8860B' }}
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Smith"
                        className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 bg-white"
                        style={{
                          border: '1px solid rgba(184,134,11,0.3)',
                          color: '#1a1a1a',
                        }}
                        onFocus={(e) =>
                          (e.currentTarget.style.borderColor = '#B8860B')
                        }
                        onBlur={(e) =>
                          (e.currentTarget.style.borderColor =
                            'rgba(184,134,11,0.3)')
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className="block text-xs font-bold mb-2 uppercase tracking-wide"
                      style={{ color: '#B8860B' }}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 bg-white"
                      style={{
                        border: '1px solid rgba(184,134,11,0.3)',
                        color: '#1a1a1a',
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = '#B8860B')
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor =
                          'rgba(184,134,11,0.3)')
                      }
                    />
                  </div>

                  <div>
                    <label
                      className="block text-xs font-bold mb-2 uppercase tracking-wide"
                      style={{ color: '#B8860B' }}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(555) 123-4567"
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 bg-white"
                      style={{
                        border: '1px solid rgba(184,134,11,0.3)',
                        color: '#1a1a1a',
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = '#B8860B')
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor =
                          'rgba(184,134,11,0.3)')
                      }
                    />
                  </div>

                  <div>
                    <label
                      className="block text-xs font-bold mb-2 uppercase tracking-wide"
                      style={{ color: '#B8860B' }}
                    >
                      Which Plan Interests You?
                    </label>
                    <select
                      name="plan_interest"
                      required
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 cursor-pointer bg-white"
                      style={{
                        border: '1px solid rgba(184,134,11,0.3)',
                        color: '#1a1a1a',
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = '#B8860B')
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor =
                          'rgba(184,134,11,0.3)')
                      }
                    >
                      <option value="">Select a plan...</option>
                      <option value="Foundation">Foundation — $297/mo</option>
                      <option value="Ascend">Ascend — $597/mo</option>
                      <option value="Legacy Elite">Legacy Elite — $1,500/mo</option>
                      <option value="Not sure yet">Not sure yet — help me decide</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className="block text-xs font-bold mb-2 uppercase tracking-wide"
                      style={{ color: '#B8860B' }}
                    >
                      Your Main Goal{' '}
                      <span style={{ color: '#aaa' }}>({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      placeholder="Tell Benny what you're working towards... (e.g., lose 20 lbs, build muscle, get stronger, feel confident again)"
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 resize-none bg-white"
                      style={{
                        border: '1px solid rgba(184,134,11,0.3)',
                        color: '#1a1a1a',
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = '#B8860B')
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor =
                          'rgba(184,134,11,0.3)')
                      }
                      onChange={(e) => setCharCount(e.target.value.length)}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || charCount > 500}
                    className="w-full py-4 rounded-lg font-extrabold text-base transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    style={{
                      background: 'linear-gradient(90deg, #B8860B, #F5D76E)',
                      color: '#000',
                    }}
                  >
                    {loading ? 'Submitting...' : 'Book My Free Consultation Now →'}
                  </button>

                  <p className="text-xs text-center text-gray-400 leading-relaxed">
                    <i
                      className="ri-lock-fill text-xs mr-1"
                      style={{ color: '#B8860B' }}
                    ></i>
                    Your information is 100% secure. We'll never spam you
                    or share your details.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}